import { useEffect, useMemo, useState } from "react";
import { de, type Item } from "@ainkauf/core";
import { getSupabase } from "@/lib/supabase";
import { signedPhotoUrl } from "@/sync/photos";

/**
 * Zeigt das Foto eines Artikels. Solange es nur lokal liegt, aus dem
 * Zwischenspeicher, danach über eine signierte Adresse: die Ablage ist privat,
 * niemand soll fremde Einkäufe sehen können.
 */
export function ItemPhoto({ item, queued }: { item: Item; queued: Blob | null }) {
  // Das lokale Bild braucht keinen Zustand, nur eine Adresse, die mit dem Blob
  // entsteht und mit ihm verschwindet.
  const lokaleUrl = useMemo(() => (queued ? URL.createObjectURL(queued) : null), [queued]);
  useEffect(() => {
    if (lokaleUrl === null) return;
    return () => {
      URL.revokeObjectURL(lokaleUrl);
    };
  }, [lokaleUrl]);

  const [signierteUrl, setSignierteUrl] = useState<string | null>(null);
  useEffect(() => {
    const client = getSupabase();
    if (queued !== null || !client || item.photo_path === null) return;

    let aktiv = true;
    void signedPhotoUrl(client, item.photo_path).then((adresse) => {
      if (aktiv) setSignierteUrl(adresse);
    });
    return () => {
      aktiv = false;
    };
  }, [item.photo_path, queued]);

  const url = lokaleUrl ?? signierteUrl;
  if (url === null) return null;

  return (
    <figure className="flex flex-col gap-1">
      <img
        src={url}
        alt={`${de.items.photo}: ${item.name}`}
        data-testid="item-photo"
        className="max-h-56 w-full rounded-[var(--radius-md)] object-cover"
      />
      {queued !== null && <figcaption className="text-sm text-ink-2">{de.items.photoWaiting}</figcaption>}
    </figure>
  );
}
